import React, { useCallback, useEffect, useState, useMemo } from "react";
import api, { API } from "../lib/api";
import {
  Users, Plus, Upload, Copy, Check, Download, Search, Loader2,
  UserPlus, UserCheck, Sparkles, Lock, Trash2, Edit2, Table as TableIcon,
  Image as ImageIcon, X, Undo2, MoveRight, FileText, Palette,
} from "lucide-react";

// Host-side guest list & seat-finder add-on. Renders a paywall when the addon
// is not unlocked yet, otherwise a full CRUD table + stats + copy-link + QR
// download. Kept in a single file to keep the diff small — internal
// sub-components live below.
const GuestListSection = ({ event, onEventChange }) => {
  const unlocked = !!event.guest_list_unlocked;

  return (
    <div id="guest-list" data-testid="guest-list-section" className="mt-10 bg-white rounded-3xl soft-shadow p-6">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-coral-300" />
        <h3 className="font-serif italic text-navy-700 text-2xl">Guest list</h3>
        <span className="text-[10px] uppercase tracking-wider bg-coral-100 text-coral-500 rounded-full px-2 py-0.5 font-semibold">Add-on</span>
      </div>
      <p className="text-sm text-navy-700/60 mt-1">
        Manage your seating chart and give guests a scan-to-find-your-seat QR code.
      </p>
      {unlocked ? (
        <GuestListManager event={event} onEventChange={onEventChange} />
      ) : (
        <GuestListPaywall event={event} />
      )}
    </div>
  );
};

// ---- Paywall ------------------------------------------------------------

const GuestListPaywall = ({ event }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [addon, setAddon] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [paystack, setPaystack] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [a, ps] = await Promise.all([
          api.get(`/payments/addons?currency=${currency}`),
          api.get(`/paystack/config`).catch(() => ({ data: { enabled: false, supported_currencies: [] } })),
        ]);
        setAddon(a.data.guest_list);
        setPaystack(ps.data);
      } catch (err) { console.debug("[paywall] addon fetch skipped:", err?.message); }
    })();
  }, [currency]);

  const usePaystack = paystack?.enabled && (paystack?.supported_currencies || []).includes(currency);
  const currencies = ["USD", ...(paystack?.supported_currencies || [])];

  const buy = async () => {
    setBusy(true); setErr("");
    try {
      if (usePaystack) {
        const r = await api.post("/paystack/checkout", {
          purchase_type: "addon",
          event_id: event.id,
          addon: "guest_list",
          currency,
          origin_url: window.location.origin,
        });
        window.location.href = r.data.authorization_url;
      } else {
        const r = await api.post("/payments/addons/checkout", {
          event_id: event.id,
          addon: "guest_list",
          currency,
          origin_url: window.location.origin,
        });
        window.location.href = r.data.url;
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 rounded-2xl border border-cream-200 bg-cream-100 p-6">
      <div className="flex flex-wrap items-center gap-2">
        <Lock className="w-4 h-4 text-navy-700/60" />
        <span className="text-xs uppercase tracking-wider text-navy-700/60 font-semibold">Not yet unlocked</span>
      </div>
      <p className="text-navy-700 mt-3 text-sm">
        Unlock the guest list + seat-finder QR for this event.
      </p>
      <ul className="mt-3 text-sm text-navy-700/70 space-y-1.5">
        <li>· CSV or paste-a-list import</li>
        <li>· Print-ready seat-finder QR (guests scan → see their table + seat)</li>
        <li>· Optional table layout diagram upload</li>
        <li>· Check-in mode at the door</li>
        <li>· Totals dashboard (RSVPs, plus-ones, tables, seated)</li>
      </ul>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <label className="text-xs text-navy-700/60 uppercase tracking-wider">Pay in</label>
        <select value={currency} onChange={(e) => setCurrency(e.target.value)}
          data-testid="guest-list-currency"
          className="px-3 py-1.5 rounded-full border border-cream-200 focus:outline-none focus:border-coral-300 text-sm bg-white">
          {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={buy} disabled={busy}
          data-testid="guest-list-buy"
          className="inline-flex items-center gap-2 bg-coral-300 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-coral-400 disabled:opacity-60">
          <Sparkles className="w-4 h-4" />
          {busy ? "Starting checkout…" : `Unlock for ${addon ? `${addon.symbol}${addon.price}` : "$19"}`}
        </button>
        <span className="text-xs text-navy-700/50">
          One-time. Yours for the life of this event.{usePaystack ? " Powered by Paystack." : ""}
        </span>
      </div>
      {err && <div className="mt-3 text-sm bg-red-50 text-red-700 rounded-md px-3 py-2" data-testid="guest-list-buy-err">{err}</div>}
    </div>
  );
};

// ---- Manager -----------------------------------------------------------

const emptyGuest = { name: "", email: "", phone: "", table: "", seat: "", plus_ones: 0, notes: "" };

const GuestListManager = ({ event }) => {
  const [guests, setGuests] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(emptyGuest);
  const [editing, setEditing] = useState(null); // guest id being edited
  const [csvOpen, setCsvOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [qrUrl, setQrUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [publicDir, setPublicDir] = useState(!!event.guest_list_public_directory);
  // Bulk-select + undo state.
  const [selected, setSelected] = useState(new Set());
  const [undo, setUndo] = useState(null); // { snapshot, expiresAt }
  // Layout image (venue seating diagram) — optional host upload.
  const [layoutUrl, setLayoutUrl] = useState(event.guest_list_layout_image || "");
  const [layoutBusy, setLayoutBusy] = useState(false);
  // Bulk-reassign to another table.
  const [reassignTable, setReassignTable] = useState("");
  // Print-cards dropdown state.
  const [cardsOpen, setCardsOpen] = useState(false);
  const [cardStyle, setCardStyle] = useState("elegant"); // "elegant" | "confetti"
  // Poster template picker state.
  const [posterOpen, setPosterOpen] = useState(false);
  const [templates, setTemplates] = useState([]);

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [g, s, u] = await Promise.all([
        api.get(`/events/${event.id}/guests`),
        api.get(`/events/${event.id}/guests/stats`),
        api.get(`/events/${event.id}/guest-list/qr-url`),
      ]);
      setGuests(g.data); setStats(s.data); setQrUrl(u.data.url);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load guest list.");
    } finally { setBusy(false); }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const payload = { ...form, plus_ones: Number(form.plus_ones || 0) };
    try {
      if (editing) {
        await api.put(`/events/${event.id}/guests/${editing}`, payload);
      } else {
        await api.post(`/events/${event.id}/guests`, payload);
      }
      setForm(emptyGuest); setEditing(null);
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const removeGuest = async (id) => {
    if (!window.confirm("Delete this guest?")) return;
    try {
      await api.delete(`/events/${event.id}/guests/${id}`);
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Delete failed."); }
  };

  // ---- Bulk delete + undo ------------------------------------------
  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = (checked) => {
    if (!checked) return setSelected(new Set());
    setSelected(new Set(filtered.map((g) => g.id)));
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/events/${event.id}/guests/bulk-delete`, { ids });
      // Show a 10-second undo toast with the snapshot in state.
      setUndo({ snapshot: r.data.snapshot, expiresAt: Date.now() + 10000 });
      setSelected(new Set());
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Bulk delete failed.");
    } finally { setBusy(false); }
  };

  const doUndo = async () => {
    if (!undo?.snapshot?.length) return;
    setBusy(true);
    try {
      await api.post(`/events/${event.id}/guests/bulk-restore`, { guests: undo.snapshot });
      setUndo(null);
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Undo failed."); }
    finally { setBusy(false); }
  };

  // Auto-dismiss the undo toast after 10s.
  useEffect(() => {
    if (!undo) return;
    const timeout = setTimeout(() => setUndo(null), Math.max(0, undo.expiresAt - Date.now()));
    return () => clearTimeout(timeout);
  }, [undo]);

  // ---- Bulk reassign to another table ------------------------------
  const bulkReassign = async () => {
    const ids = Array.from(selected);
    const tbl = (reassignTable || "").trim();
    if (ids.length === 0 || !tbl) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/events/${event.id}/guests/bulk-reassign`, { ids, table: tbl });
      setSelected(new Set());
      setReassignTable("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Bulk reassign failed.");
    } finally { setBusy(false); }
  };

  // Distinct tables in-use — powers the "Print cards" dropdown and the
  // suggestion list next to the reassign input.
  const tablesInUse = useMemo(() => {
    const m = new Map();
    for (const g of guests) {
      const t = (g.table || "").trim();
      if (!t) continue;
      m.set(t, (m.get(t) || 0) + 1);
    }
    return Array.from(m.entries())
      .map(([t, c]) => ({ table: t, count: c }))
      .sort((a, b) => a.table.length - b.table.length || a.table.localeCompare(b.table));
  }, [guests]);

  // ---- Poster template picker --------------------------------------
  const openPosterPicker = async () => {
    setPosterOpen(true);
    if (templates.length) return;
    try {
      const r = await api.get(`/qr-poster/templates`);
      setTemplates(r.data.templates || []);
    } catch {
      // Fallback: hard-coded list matching qr_poster_service.TEMPLATES.
      setTemplates([
        { id: "coral-blush",      label: "Coral Blush",      category: "Birthday" },
        { id: "elegant-cream",    label: "Elegant Cream",    category: "Wedding" },
        { id: "midnight-gold",    label: "Midnight Gold",    category: "Formal" },
        { id: "sage-botanical",   label: "Sage Botanical",   category: "Baby Shower" },
        { id: "minimalist-white", label: "Minimalist White", category: "Modern" },
        { id: "sunset-gradient",  label: "Sunset Gradient",  category: "Summer" },
        { id: "charcoal-pop",     label: "Charcoal Pop",     category: "Corporate" },
        { id: "pastel-confetti",  label: "Pastel Confetti",  category: "Kids Party" },
      ]);
    }
  };

  const posterHref = (templateId) =>
    `${API}/events/${event.id}/qr-poster/${templateId}.png?mode=seat&base_url=${encodeURIComponent(window.location.origin)}`;

  const previewHref = (templateId) =>
    `${API}/events/${event.id}/qr-poster/${templateId}.png?mode=seat&size=preview&base_url=${encodeURIComponent(window.location.origin)}`;

  // ---- Print table cards -------------------------------------------
  const cardsHref = (table) => {
    const params = new URLSearchParams();
    if (table) params.set("table", table);
    if (cardStyle && cardStyle !== "elegant") params.set("style", cardStyle);
    const qs = params.toString();
    const base = `${API}/events/${event.id}/guests/table-cards.pdf`;
    return qs ? `${base}?${qs}` : base;
  };

  // ---- Layout image upload ----------------------------------------
  const uploadLayout = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setErr("Layout image must be under 8MB.");
      return;
    }
    setLayoutBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/events/${event.id}/guest-list/layout-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLayoutUrl(r.data.url);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Layout upload failed.");
    } finally { setLayoutBusy(false); }
  };

  const removeLayout = async () => {
    if (!window.confirm("Remove the seating diagram?")) return;
    try {
      await api.delete(`/events/${event.id}/guest-list/layout-image`);
      setLayoutUrl("");
    } catch (err) { console.debug("[guest-list] delete layout swallow:", err?.message); }
  };

  const toggleCheckIn = async (g) => {
    try {
      await api.post(`/events/${event.id}/guests/${g.id}/${g.checked_in ? "undo-check-in" : "check-in"}`);
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Check-in failed."); }
  };

  const uploadCsv = async (file) => {
    const text = await file.text();
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/events/${event.id}/guests/bulk`, { csv_text: text });
      setCsvOpen(false);
      await load();
      if (r.data.errors?.length) setErr(`Imported ${r.data.added}. ${r.data.errors.length} rows skipped.`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "CSV import failed.");
    } finally { setBusy(false); }
  };

  const importPaste = async () => {
    const lines = pasteText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return;
    // Each line: "Name" or "Name<TAB>table<TAB>seat"
    const guests = lines.map((l) => {
      const parts = l.split(/\t| {2,}|,/).map((p) => p.trim());
      return {
        name: parts[0] || "",
        table: parts[1] || null,
        seat: parts[2] || null,
        plus_ones: parseInt(parts[3] || "0", 10) || 0,
        notes: parts.slice(4).join(" ") || null,
      };
    }).filter((g) => g.name);
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/events/${event.id}/guests/bulk`, { guests });
      setPasteOpen(false); setPasteText("");
      await load();
      if (r.data.errors?.length) setErr(`Imported ${r.data.added}. ${r.data.errors.length} rows skipped.`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Import failed.");
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(qrUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };

  const downloadQr = async () => {
    // Fetch as blob via api so cookies flow (need auth on this endpoint).
    const r = await api.get(`/events/${event.id}/guest-list/qr.png`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `seat-finder-${event.slug}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const togglePublic = async (v) => {
    setPublicDir(v);
    try { await api.patch(`/events/${event.id}/guest-list/settings`, { public_directory: v }); }
    catch { setPublicDir(!v); }
  };

  const filtered = q.trim() ? guests.filter((g) =>
    g.name.toLowerCase().includes(q.toLowerCase()) ||
    (g.table || "").toLowerCase().includes(q.toLowerCase()) ||
    (g.email || "").toLowerCase().includes(q.toLowerCase())
  ) : guests;

  return (
    <div className="mt-5 space-y-5" data-testid="guest-list-manager">
      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ["Guests", stats.total, "text-navy-700"],
            ["Expected", stats.expected_attendance, "text-coral-500"],
            ["Checked-in", `${stats.checked_in}/${stats.total} · ${stats.check_in_rate_pct}%`, "text-emerald-700"],
            ["Tables", stats.tables, "text-blue-700"],
          ].map(([k, v, tone]) => (
            <div key={k} className="rounded-xl border border-cream-200 bg-cream-100 p-3">
              <div className="text-[10px] uppercase tracking-wider text-navy-700/50">{k}</div>
              <div className={`text-xl font-bold mt-1 ${tone}`}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {/* QR + link + settings */}
      <div className="rounded-2xl border border-coral-200 bg-coral-50 p-4 flex flex-wrap gap-3 items-center">
        <TableIcon className="w-5 h-5 text-coral-500" />
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wider text-coral-500 font-semibold">Seat-finder link</div>
          <div className="text-sm text-navy-700 truncate">{qrUrl || "—"}</div>
        </div>
        <button onClick={copyLink} data-testid="guest-list-copy-link"
          className="inline-flex items-center gap-1.5 bg-white text-navy-700 border border-navy-700/10 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-cream-100">
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy link"}
        </button>
        <button onClick={downloadQr} data-testid="guest-list-download-qr"
          className="inline-flex items-center gap-1.5 bg-coral-300 text-white px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-coral-400">
          <Download className="w-3.5 h-3.5" /> QR PNG
        </button>
        <button
          onClick={openPosterPicker}
          data-testid="guest-list-download-poster"
          className="inline-flex items-center gap-1.5 bg-navy-700 text-white px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-navy-800">
          <Palette className="w-3.5 h-3.5" /> Print poster
        </button>
        <div className="relative">
          <button
            onClick={() => setCardsOpen((v) => !v)}
            data-testid="guest-list-print-cards-btn"
            className="inline-flex items-center gap-1.5 bg-white text-navy-700 border border-navy-700/10 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-cream-100">
            <FileText className="w-3.5 h-3.5" /> Print cards
          </button>
          {cardsOpen && (
            <div
              data-testid="guest-list-print-cards-menu"
              className="absolute right-0 mt-2 w-64 rounded-2xl bg-white shadow-xl border border-cream-200 z-20 py-2 max-h-72 overflow-y-auto"
              onMouseLeave={() => setCardsOpen(false)}
            >
              <div className="px-4 pt-1 pb-2 text-[10px] uppercase tracking-wider text-navy-700/50 font-semibold">
                A4 PDF · 6 cards per sheet
              </div>
              <div className="px-3 pb-2">
                <div className="grid grid-cols-2 gap-1.5 bg-cream-100 rounded-full p-1">
                  {[
                    { id: "elegant", label: "Elegant" },
                    { id: "confetti", label: "Confetti" },
                  ].map((s) => (
                    <button
                      key={s.id}
                      onClick={(e) => { e.stopPropagation(); setCardStyle(s.id); }}
                      data-testid={`guest-list-card-style-${s.id}`}
                      className={`text-xs font-semibold py-1 rounded-full transition-colors ${
                        cardStyle === s.id
                          ? "bg-white text-navy-700 shadow-sm"
                          : "text-navy-700/60 hover:text-navy-700"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-navy-700/50 mt-1.5 text-center">
                  {cardStyle === "confetti" ? "Bold + playful — kids' parties" : "Cream + serif — weddings, formal"}
                </div>
              </div>
              <a
                href={cardsHref()}
                data-testid="guest-list-print-cards-all"
                className="flex items-center justify-between px-4 py-2 text-sm text-navy-700 hover:bg-cream-100"
                onClick={() => setCardsOpen(false)}
              >
                <span className="font-semibold">All tables</span>
                <span className="text-xs text-navy-700/50">
                  {tablesInUse.reduce((s, t) => s + t.count, 0)} guests
                </span>
              </a>
              {tablesInUse.length > 0 && (
                <div className="border-t border-cream-200 my-1" />
              )}
              {tablesInUse.map((t) => (
                <a
                  key={t.table}
                  href={cardsHref(t.table)}
                  data-testid={`guest-list-print-cards-table-${t.table}`}
                  className="flex items-center justify-between px-4 py-1.5 text-sm text-navy-700 hover:bg-cream-100"
                  onClick={() => setCardsOpen(false)}
                >
                  <span className="truncate">Table {t.table}</span>
                  <span className="text-xs text-navy-700/50 ml-2">{t.count}</span>
                </a>
              ))}
              {tablesInUse.length === 0 && (
                <div className="px-4 py-3 text-xs text-navy-700/50">
                  Assign at least one guest to a table to enable per-table cards.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Table layout diagram — optional venue seating map */}
      <div className="rounded-2xl border border-cream-200 p-4">
        <div className="flex items-center gap-2 mb-2">
          <ImageIcon className="w-4 h-4 text-coral-300" />
          <div className="text-xs uppercase tracking-wider text-navy-700/60 font-semibold">Seating diagram</div>
          <span className="text-[11px] text-navy-700/50">Optional · shown to guests when the directory is public</span>
        </div>
        {layoutUrl ? (
          <div className="flex flex-wrap gap-3 items-start">
            <img src={layoutUrl} alt="Seating diagram"
              data-testid="guest-list-layout-image"
              className="max-h-40 rounded-lg border border-cream-200" />
            <button onClick={removeLayout}
              data-testid="guest-list-layout-remove"
              className="inline-flex items-center gap-1.5 text-navy-700/60 hover:text-red-500 text-xs">
              <X className="w-3.5 h-3.5" /> Remove diagram
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 cursor-pointer bg-white border border-cream-200 rounded-full px-3 py-1.5 text-xs font-semibold text-navy-700 hover:bg-cream-100">
            <Upload className="w-3.5 h-3.5" />
            {layoutBusy ? "Uploading…" : "Upload diagram (PNG/JPG, ≤ 8MB)"}
            <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadLayout(e.target.files[0])}
              data-testid="guest-list-layout-upload"
              className="hidden" />
          </label>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm text-navy-700/80 cursor-pointer" data-testid="guest-list-public-toggle-wrap">
        <input type="checkbox" checked={publicDir} onChange={(e) => togglePublic(e.target.checked)}
          data-testid="guest-list-public-toggle"
          className="w-4 h-4 accent-coral-300" />
        Let guests browse the full directory (privacy off)
      </label>

      {/* Add / edit form */}
      <form onSubmit={submit} className="rounded-2xl border border-cream-200 bg-cream-100 p-4" data-testid="guest-form">
        <div className="text-xs uppercase tracking-wider text-navy-700/60 font-semibold mb-3">{editing ? "Edit guest" : "Add guest"}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
          <input required placeholder="Name*" value={form.name}
            data-testid="guest-form-name"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="px-3 py-2 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm" />
          <input placeholder="Email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="px-3 py-2 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm" />
          <input placeholder="Phone" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="px-3 py-2 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm" />
          <input placeholder="Table" value={form.table}
            onChange={(e) => setForm({ ...form, table: e.target.value })}
            className="px-3 py-2 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm" />
          <input placeholder="Seat" value={form.seat}
            onChange={(e) => setForm({ ...form, seat: e.target.value })}
            className="px-3 py-2 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm" />
          <input type="number" min={0} placeholder="Plus-ones" value={form.plus_ones}
            onChange={(e) => setForm({ ...form, plus_ones: e.target.value })}
            className="px-3 py-2 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm" />
          <input placeholder="Notes (dietary, gift, etc.)" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="md:col-span-2 px-3 py-2 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm" />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button disabled={busy} type="submit"
            data-testid="guest-form-submit"
            className="inline-flex items-center gap-1.5 bg-navy-700 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-navy-800 disabled:opacity-60">
            {editing ? <Edit2 className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />} {editing ? "Save changes" : "Add guest"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(emptyGuest); }}
              className="text-xs text-navy-700/60 underline">Cancel</button>
          )}
          <button type="button" onClick={() => setCsvOpen(true)}
            data-testid="guest-import-csv-btn"
            className="ml-auto inline-flex items-center gap-1.5 bg-white text-navy-700 border border-navy-700/10 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-cream-100">
            <Upload className="w-3.5 h-3.5" /> Import CSV
          </button>
          <button type="button" onClick={() => setPasteOpen(true)}
            data-testid="guest-import-paste-btn"
            className="inline-flex items-center gap-1.5 bg-white text-navy-700 border border-navy-700/10 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-cream-100">
            <UserPlus className="w-3.5 h-3.5" /> Paste a list
          </button>
        </div>
      </form>

      {err && <div className="text-sm bg-red-50 text-red-700 rounded-md px-3 py-2" data-testid="guest-list-err">{err}</div>}

      {/* Search + list */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy-700/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="guest-search"
            placeholder="Search by name, email or table…"
            className="w-full pl-9 pr-3 py-2 rounded-full border border-cream-200 focus:outline-none focus:border-coral-300 text-sm" />
        </div>
        <span className="text-xs text-navy-700/50">{filtered.length}/{guests.length}</span>
      </div>

      {/* Bulk-select toolbar */}
      {selected.size > 0 && (
        <div data-testid="guest-bulk-toolbar" className="flex flex-wrap items-center gap-3 bg-navy-700 text-white rounded-2xl px-4 py-2.5">
          <span className="text-sm font-semibold">{selected.size} selected</span>
          <button onClick={() => setSelected(new Set())}
            data-testid="guest-bulk-clear"
            className="text-xs underline text-white/80 hover:text-white">Clear</button>
          <div className="flex items-center gap-1.5 ml-auto">
            <MoveRight className="w-3.5 h-3.5 text-white/70" />
            <input
              type="text"
              list={`reassign-table-options-${event.id}`}
              value={reassignTable}
              onChange={(e) => setReassignTable(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") bulkReassign(); }}
              placeholder="Move to table…"
              data-testid="guest-bulk-reassign-input"
              className="w-36 sm:w-44 px-3 py-1.5 rounded-full text-xs text-navy-700 bg-white/95 placeholder:text-navy-700/40 focus:outline-none focus:ring-2 focus:ring-coral-300"
            />
            <datalist id={`reassign-table-options-${event.id}`}>
              {tablesInUse.map((t) => (
                <option key={t.table} value={t.table} />
              ))}
            </datalist>
            <button
              onClick={bulkReassign}
              disabled={busy || !reassignTable.trim()}
              data-testid="guest-bulk-reassign"
              className="inline-flex items-center gap-1.5 bg-coral-300 hover:bg-coral-400 px-3 py-1.5 rounded-full text-xs font-semibold disabled:opacity-60"
            >
              Reassign
            </button>
          </div>
          <button onClick={bulkDelete} disabled={busy}
            data-testid="guest-bulk-delete"
            className="inline-flex items-center gap-1.5 bg-red-500 hover:bg-red-600 px-3 py-1.5 rounded-full text-xs font-semibold disabled:opacity-60">
            <Trash2 className="w-3.5 h-3.5" /> Delete {selected.size}
          </button>
        </div>
      )}

      <div className="rounded-2xl border border-cream-200 overflow-hidden">
        {busy && guests.length === 0 ? (
          <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-coral-300 mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-navy-700/50">
            {guests.length === 0 ? "No guests yet. Add one above or import a CSV." : "No guests match that search."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream-100">
                <tr className="text-left text-[11px] uppercase tracking-wider text-navy-700/60">
                  <th className="py-2.5 pl-3 pr-2 w-8">
                    <input type="checkbox"
                      data-testid="guest-select-all"
                      checked={selected.size > 0 && selected.size === filtered.length}
                      onChange={(e) => selectAllVisible(e.target.checked)}
                      className="w-4 h-4 accent-coral-300" />
                  </th>
                  <th className="pr-2">Name</th>
                  <th className="pr-2">Table · Seat</th>
                  <th className="pr-2">+</th>
                  <th className="pr-2">Notes</th>
                  <th className="pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-200 bg-white">
                {filtered.map((g) => (
                  <tr key={g.id} data-testid={`guest-row-${g.id}`} className={selected.has(g.id) ? "bg-coral-50" : ""}>
                    <td className="py-2 pl-3 pr-2">
                      <input type="checkbox"
                        data-testid={`guest-select-${g.id}`}
                        checked={selected.has(g.id)}
                        onChange={() => toggleSelected(g.id)}
                        className="w-4 h-4 accent-coral-300" />
                    </td>
                    <td className="pr-2">
                      <div className="font-medium text-navy-700 truncate">{g.name}</div>
                      <div className="text-[11px] text-navy-700/50 truncate">{g.email || ""}{g.phone ? ` · ${g.phone}` : ""}</div>
                    </td>
                    <td className="pr-2 text-navy-700/70 whitespace-nowrap">{g.table || "—"} · {g.seat || "—"}</td>
                    <td className="pr-2 text-navy-700/70">{g.plus_ones || 0}</td>
                    <td className="pr-2 text-navy-700/60 max-w-[220px] truncate" title={g.notes || ""}>{g.notes || "—"}</td>
                    <td className="pr-3 text-right whitespace-nowrap">
                      <button onClick={() => toggleCheckIn(g)}
                        data-testid={`guest-check-${g.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ${g.checked_in ? "bg-emerald-100 text-emerald-800" : "bg-cream-100 text-navy-700 hover:bg-coral-100 hover:text-coral-500"}`}>
                        <UserCheck className="w-3 h-3" /> {g.checked_in ? "Here" : "Check in"}
                      </button>
                      <button onClick={() => { setEditing(g.id); setForm({ ...g, email: g.email || "", phone: g.phone || "", table: g.table || "", seat: g.seat || "", notes: g.notes || "" }); window.scrollTo({top: document.querySelector('[data-testid="guest-form"]')?.offsetTop || 0, behavior: "smooth"}); }}
                        className="ml-1 text-navy-700/50 hover:text-coral-300 p-1">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => removeGuest(g.id)}
                        data-testid={`guest-delete-${g.id}`}
                        className="text-navy-700/50 hover:text-red-500 p-1">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {csvOpen && (
        <Modal onClose={() => setCsvOpen(false)} title="Import guests from CSV">
          <p className="text-sm text-navy-700/70">
            Columns (in any order): <code>name, email, phone, table, seat, plus_ones, notes</code>. Only <code>name</code> is required.
          </p>
          <input type="file" accept=".csv,text/csv" onChange={(e) => e.target.files?.[0] && uploadCsv(e.target.files[0])}
            data-testid="guest-csv-file"
            className="mt-4 block w-full text-sm" />
          <a href="data:text/csv,name,email,phone,table,seat,plus_ones,notes%0AAda%20Lovelace,ada@x.com,,1,A,1,Vegetarian"
            download="guest-list-sample.csv"
            className="text-xs text-coral-300 underline mt-2 inline-block">Download sample CSV</a>
        </Modal>
      )}
      {pasteOpen && (
        <Modal onClose={() => setPasteOpen(false)} title="Paste a list of guests">
          <p className="text-sm text-navy-700/70">
            One guest per line. Tab, comma, or multi-space separated: <code>Name · Table · Seat · Plus-ones · Notes</code>.
          </p>
          <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)}
            data-testid="guest-paste-text"
            rows={8} placeholder={"Ada Lovelace\tTable 1\tA\t1\nBrenda Bell\tTable 1\tB\nCharlie Chen\tTable 2\tA\t2\tNo nuts"}
            className="mt-3 w-full px-3 py-2 rounded-lg border border-cream-200 text-sm font-mono" />
          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={() => setPasteOpen(false)} className="text-xs text-navy-700/60 underline">Cancel</button>
            <button onClick={importPaste} disabled={!pasteText.trim() || busy}
              data-testid="guest-paste-submit"
              className="bg-coral-300 text-white px-4 py-2 rounded-full text-xs font-semibold hover:bg-coral-400 disabled:opacity-60">
              Import
            </button>
          </div>
        </Modal>
      )}
      {/* Poster template picker — pick any of the 8 designs for the seat-finder QR */}
      {posterOpen && (
        <Modal onClose={() => setPosterOpen(false)} title="Pick your seat-finder poster" size="lg">
          <p className="text-sm text-navy-700/70">
            Print-ready A4 · scan takes guests to their seat, not the photo gallery.
          </p>
          <div
            data-testid="seat-poster-picker"
            className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[70vh] overflow-y-auto pr-1"
          >
            {templates.length === 0 ? (
              <div className="col-span-full py-8 text-center text-navy-700/50">
                <Loader2 className="w-5 h-5 animate-spin inline-block" />
              </div>
            ) : templates.map((t) => (
              <a
                key={t.id}
                href={posterHref(t.id)}
                download={`seat-finder-${event.slug}-${t.id}.png`}
                onClick={() => setPosterOpen(false)}
                data-testid={`seat-poster-template-${t.id}`}
                className="group rounded-xl border border-cream-200 hover:border-coral-300 overflow-hidden bg-cream-100"
              >
                <div className="aspect-[1240/1754] bg-white overflow-hidden">
                  <img
                    src={previewHref(t.id)}
                    alt={t.label}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                  />
                </div>
                <div className="px-2.5 py-2">
                  <div className="text-xs font-semibold text-navy-700 truncate">{t.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-navy-700/50">{t.category}</div>
                </div>
              </a>
            ))}
          </div>
        </Modal>
      )}

      {/* Undo toast — a 10-second window to restore a bulk-delete */}
      {undo && (
        <div data-testid="guest-undo-toast" className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-navy-700 text-white rounded-full shadow-xl px-5 py-3 flex items-center gap-3 z-50">
          <Check className="w-4 h-4 text-emerald-300" />
          <span className="text-sm">Deleted {undo.snapshot.length} guest{undo.snapshot.length === 1 ? "" : "s"}.</span>
          <button onClick={doUndo} data-testid="guest-undo-btn"
            className="inline-flex items-center gap-1 text-coral-200 hover:text-white text-sm font-semibold">
            <Undo2 className="w-3.5 h-3.5" /> Undo
          </button>
          <button onClick={() => setUndo(null)} className="text-white/60 hover:text-white text-lg leading-none pl-2">×</button>
        </div>
      )}
    </div>
  );
};

const Modal = ({ title, onClose, children, size = "md" }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-black/40" onClick={onClose}>
    <div
      className={`bg-white rounded-2xl w-full ${size === "lg" ? "max-w-3xl" : "max-w-lg"} p-6 max-h-[90vh] overflow-y-auto`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-serif italic text-navy-700 text-xl">{title}</h4>
        <button onClick={onClose} className="text-navy-700/60 hover:text-coral-300 text-2xl leading-none">×</button>
      </div>
      {children}
    </div>
  </div>
);

export default GuestListSection;
